import { useState, useCallback } from 'react';

/**
 * Hook do zarządzania integracją z API Blissaro
 * Obsługuje sprawdzanie kont, rejestrację i generowanie kodów QR
 * @param {string} backendUrl - URL backendu
 * @param {boolean} isAdminMode - Czy hook jest używany w panelu admina (domyślnie: false)
 */
export const useBlissaroIntegration = (backendUrl, isAdminMode = false) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkResult, setCheckResult] = useState(null);

  // Sprawdź czy użytkownik ma konto w Blissaro
  const checkUserAccount = useCallback(async (email) => {
    if (!email) {
      setError('Email jest wymagany');
      return;
    }

    setLoading(true);
    setError(null);
    setCheckResult(null);

    try {
      const endpoint = isAdminMode ? 'admin' : 'client';
      const response = await fetch(`${backendUrl}/${endpoint}/blissaro/check-user?email=${encodeURIComponent(email)}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Błąd sprawdzania konta');
      }
      
      const data = await response.json();
      setCheckResult(data);
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Wystąpił błąd podczas sprawdzania konta';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, isAdminMode]);

  // Zarejestruj nowego użytkownika w Blissaro
  const registerUser = useCallback(async (userData) => {
    const { name, email, password } = userData;
    
    if (!name || !email || !password) {
      const errorMsg = 'Wszystkie pola są wymagane';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    if (password.length < 8) {
      const errorMsg = 'Hasło musi mieć minimum 8 znaków';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = isAdminMode ? 'admin' : 'client';
      const response = await fetch(`${backendUrl}/${endpoint}/blissaro/register-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name, email, password }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Błąd rejestracji');
      }
      
      const data = await response.json();
      
      // Aktualizuj stan - użytkownik ma teraz konto
      setCheckResult({ exists: true, userId: data.user._id });
      
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Wystąpił błąd podczas rejestracji';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, isAdminMode]);

  // Generuj przykładowy QR kod (tylko dla adminów)
  const generatePlaceholderQR = useCallback(async (email) => {
    if (!isAdminMode) {
      const errorMsg = 'Funkcja dostępna tylko dla administratorów';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    if (!email) {
      const errorMsg = 'Email jest wymagany';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    if (!checkResult?.exists) {
      const errorMsg = 'Użytkownik musi mieć konto w Blissaro';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${backendUrl}/admin/blissaro/generate-placeholder-qr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      
      if (!response.ok) {
        throw new Error('Błąd generowania QR');
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Wystąpił błąd podczas generowania QR';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, checkResult, isAdminMode]);

  // Generuj QR kody na podstawie projektu (dla klientów)
  const generateQRFromProject = useCallback(async (email, projectId) => {
    if (!email || !projectId) {
      const errorMsg = 'Email i ID projektu są wymagane';
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${backendUrl}/client/blissaro/generate-qr-from-project`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          email, 
          projectId 
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Błąd generowania QR');
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Wystąpił błąd podczas generowania kodów QR';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, checkResult]);

  // Sprawdź status API Blissaro
  const checkApiStatus = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = isAdminMode ? 'admin' : 'client';
      const response = await fetch(`${backendUrl}/${endpoint}/blissaro/api-status`, {
        credentials: 'include',
      });
      
      const data = await response.json();
      return data;
    } catch (err) {
      const errorMsg = err.message || 'Błąd sprawdzania statusu API';
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, isAdminMode]);

  // Resetuj stan
  const resetState = useCallback(() => {
    setError(null);
    setCheckResult(null);
    setLoading(false);
  }, []);

  return {
    // Stan
    loading,
    error,
    checkResult,
    
    // Akcje
    checkUserAccount,
    registerUser,
    generatePlaceholderQR,
    generateQRFromProject,
    checkApiStatus,
    resetState,
    
    // Flagi pomocnicze
    hasUserAccount: checkResult?.exists || false,
    canGenerateQR: checkResult?.exists || false
  };
}; 